import React, { useEffect, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { serviceRequestsApi, customersApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, ClipboardPlus, Check, Ban, X, CheckCircle2 } from 'lucide-react';
import { fmtMoney, fmtDate } from '@/lib/format';
import { uploadUrl } from '@/lib/api';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { ADMIN_PORTAL } from '@/constants/testIds';

export default function ServiceRequestsPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({ quoted_amount: 0, quote_note: '', line_items: [] });

  const load = async () => {
    try {
      setItems(await serviceRequestsApi.list());
    } catch {
      // surfaced by the api response interceptor
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); customersApi.list().then(setCustomers).catch(() => {}); }, []);

  const openQuote = (r) => {
    setOpenId(r.id);
    setForm({
      quoted_amount: r.quoted_amount ?? 0,
      quote_note: r.quote_note || '',
      line_items: r.line_items && r.line_items.length ? r.line_items : [{ label: r.title, qty: 1, unit_price: 0, taxable: true }],
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const total = form.line_items.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_price) || 0), 0);
    try {
      await serviceRequestsApi.quote(openId, {
        quoted_amount: form.quoted_amount || total,
        quote_note: form.quote_note,
        line_items: form.line_items,
      });
      toast.success('Quote sent to customer');
      setOpenId(null);
      load();
    } catch { toast.error('Failed'); }
  };

  const decline = async (id) => { await serviceRequestsApi.decline(id); toast.success('Declined'); load(); };

  const grouped = { submitted: [], quoted: [], accepted: [], declined: [] };
  items.forEach(r => { (grouped[r.status] || (grouped[r.status] = [])).push(r); });

  return (
    <AppShell title="Service Requests">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-semibold">Service Requests</h1>
        <p className="text-sm text-muted-foreground">Customer-submitted upsell opportunities. Quote them here → they accept in the portal → a job is created automatically.</p>
      </div>

      {!loaded ? (
        <ListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState icon={ClipboardPlus} title="No service requests yet" description="Once a customer submits a request from the portal it will appear here." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {['submitted', 'quoted', 'accepted', 'declined'].map(status => (
            <div key={status} className="rounded-xl border bg-surface-sunken">
              <div className="px-3 py-2 border-b border-border-strong flex items-center justify-between">
                <div className="text-sm font-semibold capitalize">{status}</div>
                <div className="text-xs text-muted-foreground">{(grouped[status] || []).length}</div>
              </div>
              <div className="p-2 space-y-2 min-h-[200px]">
                {(grouped[status] || []).map(r => {
                  const c = customers.find(x => x.id === r.customer_id);
                  return (
                    <Card key={r.id}>
                      <CardContent className="p-3">
                        <div className="font-medium text-sm">{r.title}</div>
                        <div className="text-[11px] text-muted-foreground">{c?.name || '—'} • {fmtDate(r.created_at)}</div>
                        {r.description && <div className="text-xs mt-1">{r.description}</div>}
                        {r.photo_ids && r.photo_ids.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {r.photo_ids.slice(0, 3).map(pid => <img key={pid} src={uploadUrl(`/uploads/${pid}.jpg`)} onError={(e) => { e.target.style.display = 'none'; }} alt="" className="h-14 w-14 object-cover rounded-md border" />)}
                            {r.photo_ids.length > 3 && <div className="text-[10px] text-muted-foreground self-center">+{r.photo_ids.length - 3}</div>}
                          </div>
                        )}
                        {r.quoted_amount != null && <div className="mt-2 text-sm font-semibold tabular-nums">{fmtMoney(r.quoted_amount)}</div>}
                        {r.status === 'submitted' && (
                          <div className="flex gap-1 mt-2">
                            <Button size="sm" variant="outline" onClick={() => decline(r.id)}><Ban className="h-3 w-3 mr-1" />Decline</Button>
                            <Button size="sm" onClick={() => openQuote(r)}><Plus className="h-3 w-3 mr-1" />Send quote</Button>
                          </div>
                        )}
                        {r.status === 'quoted' && (
                          <div className="flex gap-1 mt-2">
                            <Button size="sm" variant="outline" onClick={() => openQuote(r)}>Edit quote</Button>
                          </div>
                        )}
                        {r.status === 'accepted' && r.converted_job_id && <div className="mt-1 flex items-center gap-1 text-[11px] text-[hsl(var(--fc-success))]"><CheckCircle2 className="h-3 w-3" />Converted to job</div>}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!openId} onOpenChange={v => !v && setOpenId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send a quote</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-2">
              <Label>Line items</Label>
              {form.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2">
                  <Input className="col-span-2 sm:col-span-6" placeholder="Description" value={li.label} onChange={e => { const arr = [...form.line_items]; arr[i].label = e.target.value; setForm({ ...form, line_items: arr }); }} />
                  <Input className="col-span-1 sm:col-span-2" type="number" step="0.01" value={li.qty} onChange={e => { const arr = [...form.line_items]; arr[i].qty = parseFloat(e.target.value) || 0; setForm({ ...form, line_items: arr }); }} />
                  <Input className="col-span-1 sm:col-span-3" type="number" step="0.01" value={li.unit_price} onChange={e => { const arr = [...form.line_items]; arr[i].unit_price = parseFloat(e.target.value) || 0; setForm({ ...form, line_items: arr }); }} />
                  <Button type="button" size="icon" variant="ghost" className="col-span-1 justify-self-end sm:justify-self-auto" onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, x) => x !== i) })}><X className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, line_items: [...form.line_items, { label: '', qty: 1, unit_price: 0, taxable: true }] })}><Plus className="h-3 w-3 mr-1" />Add item</Button>
            </div>
            <div><Label>Total ($)</Label><Input type="number" step="0.01" value={form.quoted_amount} onChange={e => setForm({ ...form, quoted_amount: parseFloat(e.target.value) || 0 })} data-testid={ADMIN_PORTAL.serviceRequestQuoteAmount} /></div>
            <div><Label>Note to customer</Label><Textarea rows={2} value={form.quote_note} onChange={e => setForm({ ...form, quote_note: e.target.value })} /></div>
            <DialogFooter><Button type="submit" data-testid={ADMIN_PORTAL.serviceRequestQuoteSubmit}>Send quote</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
